import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { StorefrontIcon as Storefront, ArrowRightIcon as ArrowRight, WarningIcon as Warning, CheckCircleIcon as CheckCircle, BoneIcon as Bone, TShirtIcon as TShirt, GraduationCapIcon as GraduationCap } from "@phosphor-icons/react";

/**
 * Admin-only one-shot tool: migrate the Armstrong family's food
 * catalog off the admin/platform account onto a Dog Shop multi-tenant
 * seller storefront owned by a real seller user. After migration the
 * public /armstrong page stops selling food and redirects buyers to
 * the new seller URL. Merch (Printify-synced) and Training are NOT
 * migrated by this tool — see the inline notes.
 */
export default function AdminArmstrongMigrate() {
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    destination_email: "armstrongsupremeteam@gmail.com",
    slug: "supreme-team",
    store_name: "Armstrong Supreme Team",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/armstrong/migration/preview")
      .then((r) => setPreview(r.data))
      .catch((e) => {
        toast.error(formatApiError(e));
        setPreview({ food_to_migrate: [], food_count: 0, merch_count_not_migrated: 0, training_count_not_migrated: 0 });
      });
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.destination_email.trim() || !form.slug.trim()) {
      toast.error("Destination email and slug are required.");
      return;
    }
    if (!window.confirm(
      `Migrate ${preview?.food_count || 0} food product(s) to /store/${form.slug}? `
      + `This soft-archives the originals on the Armstrong storefront.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/armstrong/migrate-to-storefront", form);
      setResult(data);
      toast.success(`Migrated ${data.migrated_count} item(s) to ${data.public_url}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!preview) return <AppShell><div className="max-w-4xl mx-auto px-4 py-10">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-armstrong-migrate">
        <header className="mb-6">
          <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink mb-2 inline-block">
            ← Back to admin
          </Link>
          <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight">
            Migrate Armstrong → Dog Shop seller
          </h1>
          <p className="text-[13px] sm:text-sm text-[#6B6B6B] mt-1 max-w-2xl">
            Moves the founder&rsquo;s food catalog off the admin/platform
            account onto a real seller storefront. After this runs, the
            public <code>/armstrong</code> page hides its food and merch
            sections and points buyers to <code>/store/{form.slug || "…"}</code>.
            Training is converted to a contact card (no longer for sale on the
            Registry). Originals are <strong>soft-archived</strong>, not deleted,
            so rollback is possible.
          </p>
        </header>

        {/* What moves / what doesn't */}
        <section className="rounded-2xl border border-[#EAEAEA] bg-white p-5 mb-5">
          <h2 className="font-spectral text-lg tracking-tight mb-3">What this tool does</h2>
          <ul className="space-y-2 text-[13px]">
            <li className="flex items-start gap-2.5" data-testid="migration-summary-food">
              <Bone size={16} weight="duotone" className="text-gold-dark mt-0.5 shrink-0" />
              <span>
                <strong>{preview.food_count}</strong> dog-food product
                {preview.food_count === 1 ? "" : "s"} copied to the new store.
              </span>
            </li>
            <li className="flex items-start gap-2.5 text-[#6B6B6B]" data-testid="migration-summary-merch">
              <TShirt size={16} weight="duotone" className="mt-0.5 shrink-0" />
              <span>
                <strong>{preview.merch_count_not_migrated}</strong> Printify merch SKU
                {preview.merch_count_not_migrated === 1 ? "" : "s"} <em>not</em> migrated —
                Printify connection has to be re-pointed to the seller&rsquo;s own Printify
                account. Out of scope here.
              </span>
            </li>
            <li className="flex items-start gap-2.5 text-[#6B6B6B]" data-testid="migration-summary-training">
              <GraduationCap size={16} weight="duotone" className="mt-0.5 shrink-0" />
              <span>
                <strong>{preview.training_count_not_migrated}</strong> training subscription
                {preview.training_count_not_migrated === 1 ? "" : "s"} <em>not</em> migrated —
                services can&rsquo;t be sold on Dog Shop. The public page converts to a
                phone/email contact card instead.
              </span>
            </li>
          </ul>
          {preview.food_count === 0 && (
            <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
              No food products are currently on the Armstrong storefront. The new store will
              still be created so the public page can redirect to it.
            </p>
          )}
        </section>

        {/* Destination */}
        <section className="rounded-2xl border border-[#EAEAEA] bg-white p-5 mb-5">
          <h2 className="font-spectral text-lg tracking-tight mb-3">Destination seller</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Seller account email</Label>
              <Input
                type="email"
                value={form.destination_email}
                onChange={(e) => update("destination_email", e.target.value)}
                data-testid="armstrong-migrate-email"
                className="font-tabular"
              />
              <p className="text-[10.5px] text-[#9A9A9A] mt-1">
                Must already be registered on production.
              </p>
            </div>
            <div>
              <Label className="text-xs">Public slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => update("slug", e.target.value.toLowerCase())}
                data-testid="armstrong-migrate-slug"
                className="font-tabular"
                placeholder="supreme-team"
              />
              <p className="text-[10.5px] text-[#9A9A9A] mt-1">
                Final URL: <code>/store/{form.slug || "…"}</code>
              </p>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">Display name</Label>
            <Input
              value={form.store_name}
              onChange={(e) => update("store_name", e.target.value)}
              data-testid="armstrong-migrate-name"
            />
          </div>
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
            <Warning size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
            <p className="text-[12px] text-amber-900 leading-relaxed">
              The seller account needs an active <strong>Dog Shop subscription</strong> and a
              completed <strong>Stripe Connect</strong> account before public checkout will
              work. The data move happens regardless &mdash; this is just a heads-up to finish
              onboarding before announcing the new URL.
            </p>
          </div>
        </section>

        <div className="sticky bottom-4 bg-white border-2 border-ink rounded-2xl p-4 shadow-lg flex items-center justify-between flex-wrap gap-3">
          <p className="text-[12.5px]">
            Ready when you are.
          </p>
          <Button
            onClick={submit}
            disabled={busy || !form.destination_email.trim() || !form.slug.trim()}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="armstrong-migrate-submit-btn"
          >
            {busy ? "Migrating…" : "Run migration"}
            <ArrowRight size={13} weight="bold" className="ml-1" />
          </Button>
        </div>

        {result && (
          <section
            className="mt-6 rounded-2xl border border-[#BFE8C9] bg-[#EEFBEC] p-5"
            data-testid="armstrong-migrate-result"
          >
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle size={20} weight="fill" className="text-[#0F8043] mt-0.5" />
              <div>
                <h3 className="font-spectral text-lg tracking-tight">Migration complete</h3>
                <p className="text-[13px] text-[#3F3F3F]">
                  Moved <strong>{result.migrated_count}</strong> product
                  {result.migrated_count === 1 ? "" : "s"} to{" "}
                  <Link to={result.public_url} className="underline font-semibold">
                    {result.public_url}
                  </Link>
                  {result.skipped_count > 0 && (
                    <> · skipped <strong>{result.skipped_count}</strong></>
                  )}
                </p>
              </div>
            </div>
            <div className="rounded-lg bg-white border border-[#EAEAEA] p-3 text-[12.5px]">
              <p className="font-bold text-ink mb-1">Next steps:</p>
              <ol className="space-y-1 list-decimal list-inside text-[#3a3a3a]">
                <li>Have <strong>{result.destination_email}</strong> log in.</li>
                <li>Subscribe to The Dog Shop on <code>/billing</code> ($19/mo or $190/yr).</li>
                <li>
                  Complete Stripe Connect onboarding from <code>/me/store</code> so payouts can flow.
                </li>
                <li>
                  Visit <Link to={result.public_url} className="underline">{result.public_url}</Link>{" "}
                  to verify the catalog rendered correctly.
                </li>
              </ol>
            </div>
            <p className="text-[11px] text-[#6B6B6B] mt-3 leading-relaxed">
              The Armstrong public page now shows a redirect banner pointing
              buyers to {result.public_url}. Originals are still in the
              database with <code>migrated_to_store_id={result.store_id}</code> for
              rollback.
            </p>
          </section>
        )}
      </div>
    </AppShell>
  );
}
